// Las mismas reglas de cuentas de agencia y soporte que el servidor, sobre el almacén de la vista previa (datos de prueba en el navegador).
// Las contraseñas se guardan con SHA-256 y sal (claveHash y sal) solo para la demostración; nunca salen en las listas.
import { hashSha, iguales, nuevaSal } from "./clave-web";
import type { Fila } from "./convertir";
import { conFallo, estaBloqueado, mensajeBloqueo, sinIntentos } from "./intentos";
import type { Almacen } from "./proveedor";
import type { Rol } from "./recursos";
import { normalizarUsuario, numeroTicket, validarClave, validarRegistro, validarTicket, validarUsuario, type DatosTicket, type RegistroAgencia } from "./validar";
import { accionProveedorLocal, altaProveedorLocal, responderSolicitudLocal } from "./reservas-local";

const SECRETOS = ["claveHash", "sal", "intentosFallidos", "bloqueadoHasta"];
export const sinSecretos = (f: Fila): Fila => { const x = { ...f }; for (const k of SECRETOS) delete x[k]; return x; };

const ahora = () => new Date().toISOString();
const bit = (a: Almacen, tabla: string, registroId: string, accion: string, antes?: unknown, despues?: unknown) =>
  a.tabla("bitacora").unshift({ id: a.nuevoId(), creadoEn: ahora(), tabla, registroId, accion, motivo: null, antes: antes ?? null, despues: despues ?? null });
const libre = (a: Almacen, usuario: string, id?: string) => !a.tabla("agencias").some((x) => x.usuarioPortal === usuario && x.id !== id);

export async function registrarLocal(a: Almacen, entrada: Partial<RegistroAgencia>) {
  const { error, datos } = validarRegistro(entrada);
  if (error) throw new Error(error);
  if (!libre(a, datos.usuario)) throw new Error("Ese usuario ya está en uso. Elige otro.");
  const sal = nuevaSal();
  const fila: Fila = { id: a.nuevoId(), nombre: datos.nombre, contacto: datos.contacto, telefono: datos.telefono, correo: datos.correo, ciudad: datos.ciudad || null, rfc: null, referidoPor: null, notas: null, comisionPorciento: null,
    usuarioPortal: datos.usuario, estadoCuenta: "pendiente", solicitadaEn: ahora(), sal, claveHash: await hashSha(datos.clave, sal) };
  a.tabla("agencias").unshift(fila);
  bit(a, "agencia", fila.id, "solicitud de acceso", undefined, { nombre: datos.nombre, usuarioPortal: datos.usuario });
  a.guardar();
  return { ok: true as const };
}

export async function entrarLocal(a: Almacen, usuarioEntrada: string, claveEntrada: string) {
  const usuario = normalizarUsuario(usuarioEntrada);
  const malo = new Error("Usuario o contraseña incorrectos");
  const ag = usuario ? a.tabla("agencias").find((x) => x.usuarioPortal === usuario) : undefined;
  if (!ag) throw malo;
  const t = Date.now();
  const previo = { fallos: Number(ag.intentosFallidos ?? 0), hasta: ag.bloqueadoHasta ? new Date(String(ag.bloqueadoHasta)).getTime() : 0 };
  if (estaBloqueado(previo, t)) throw new Error(mensajeBloqueo(previo, t));
  const bien = !!ag.claveHash && iguales(await hashSha(String(claveEntrada ?? ""), String(ag.sal ?? "")), String(ag.claveHash));
  if (!bien) {
    const n = conFallo(previo, t);
    ag.intentosFallidos = n.fallos; ag.bloqueadoHasta = n.hasta ? new Date(n.hasta).toISOString() : null; a.guardar();
    throw estaBloqueado(n, t) ? new Error(mensajeBloqueo(n, t)) : malo;
  }
  if (ag.intentosFallidos || ag.bloqueadoHasta) { ag.intentosFallidos = 0; ag.bloqueadoHasta = null; a.guardar(); }
  if (ag.estadoCuenta === "pendiente") throw new Error("Tu cuenta está pendiente de activación");
  if (ag.estadoCuenta === "suspendida") throw new Error("Tu cuenta está suspendida");
  return { id: ag.id, nombre: String(ag.nombre) };
}

/** Al crear una agencia desde el panel con usuario y contraseña (quita la contraseña en claro). */
export async function altaAgenciaLocal(a: Almacen, datos: Record<string, unknown>) {
  const clave = datos.clave ? String(datos.clave) : "";
  delete datos.clave;
  datos.solicitadaEn = ahora();
  const usuario = normalizarUsuario(datos.usuarioPortal);
  if (!usuario && !clave) { delete datos.usuarioPortal; datos.estadoCuenta = "pendiente"; return; }
  if (!usuario || !clave) throw new Error("Para dar acceso escribe el usuario y la contraseña");
  const error = validarUsuario(usuario) ?? validarClave(clave);
  if (error) throw new Error(error);
  if (!libre(a, usuario)) throw new Error("Ya existe un registro con ese valor (debe ser único).");
  const sal = nuevaSal();
  Object.assign(datos, { usuarioPortal: usuario, sal, claveHash: await hashSha(clave, sal), estadoCuenta: "activa" });
}

export const quienEsLocal = (a: Almacen, rol: Rol) => {
  const r = a.tabla("roles").find((x) => x.nombre === rol);
  return a.tabla("usuarios").find((u) => u.rolId === r?.id && u.estado !== "suspendido");
};

export async function accionLocal(a: Almacen, rol: Rol, c: string, id: string, nombre: string, d: Record<string, string>): Promise<{ mensaje: string } | null> {
  if (c === "agencias") return accionAgenciaLocal(a, id, nombre, d);
  if (c === "soporte") return accionSoporteLocal(a, id, nombre, d, rol);
  if (c === "proveedoresReserva") return accionProveedorLocal(a, id, nombre, d);
  if (c === "solicitudesReserva" && ["aceptar", "rechazar", "cancelar"].includes(nombre)) return responderSolicitudLocal(a, id, nombre, d.motivo);
  return null;
}

async function accionAgenciaLocal(a: Almacen, id: string, nombre: string, d: Record<string, string>) {
  if (!["activarCuenta", "suspender", "restablecerClave", "crearAcceso"].includes(nombre)) return null;
  const ag = a.tabla("agencias").find((x) => x.id === id);
  if (!ag) throw new Error("No existe");
  const reinicio = () => { ag.intentosFallidos = 0; ag.bloqueadoHasta = null; };
  if (nombre === "activarCuenta") {
    if (!ag.usuarioPortal) throw new Error("Esta agencia no tiene usuario. Primero crea su acceso.");
    bit(a, "agencia", id, "activar cuenta", { estadoCuenta: ag.estadoCuenta }, { estadoCuenta: "activa" });
    ag.estadoCuenta = "activa"; reinicio();
    return { mensaje: "Cuenta activada: ya puede entrar al portal" };
  }
  if (nombre === "suspender") {
    bit(a, "agencia", id, "suspender cuenta", { estadoCuenta: ag.estadoCuenta }, { estadoCuenta: "suspendida" });
    ag.estadoCuenta = "suspendida";
    return { mensaje: "Cuenta suspendida" };
  }
  if (nombre === "restablecerClave") {
    if (!ag.usuarioPortal) throw new Error("Esta agencia no tiene usuario. Primero crea su acceso.");
    const error = validarClave(d.clave ?? "");
    if (error) throw new Error(error);
    ag.sal = nuevaSal(); ag.claveHash = await hashSha(d.clave, String(ag.sal)); reinicio();
    bit(a, "agencia", id, "restablecer contraseña");
    return { mensaje: "Contraseña cambiada. Avísale a la agencia por un medio seguro." };
  }
  if (ag.usuarioPortal) throw new Error("Esta agencia ya tiene usuario");
  const usuario = normalizarUsuario(d.usuario);
  const error = validarUsuario(usuario) ?? validarClave(d.clave ?? "");
  if (error) throw new Error(error);
  if (!libre(a, usuario, id)) throw new Error("Ese usuario ya está en uso. Elige otro.");
  const sal = nuevaSal();
  Object.assign(ag, { usuarioPortal: usuario, sal, claveHash: await hashSha(d.clave, sal), estadoCuenta: "activa" }); reinicio();
  bit(a, "agencia", id, "crear acceso", undefined, { usuarioPortal: usuario });
  return { mensaje: `Acceso creado: usuario ${usuario}` };
}

// ───────── Soporte
export function siguienteNumero(a: Almacen) {
  const max = a.tabla("soporte").reduce((n, t) => Math.max(n, Number(String(t.numero ?? "").replace(/\D/g, "")) || 0), 0);
  return numeroTicket(max + 1);
}

/** Datos que el servidor completa al crear una solicitud desde el panel. */
export function prepararTicketLocal(a: Almacen, rol: Rol, datos: Record<string, unknown>) {
  const yo = quienEsLocal(a, rol);
  datos.numero = siguienteNumero(a);
  datos.usuarioId = yo?.id ?? null;
  datos.actualizadoEn = ahora();
}

export async function crearTicketLocal(a: Almacen, entrada: Partial<DatosTicket>) {
  const { error, datos } = validarTicket(entrada);
  if (error) throw new Error(error);
  let contacto = datos.contacto ?? null;
  if (datos.agenciaId) {
    const ag = a.tabla("agencias").find((x) => x.id === datos.agenciaId);
    if (!ag) throw new Error("No encontramos la agencia");
    contacto ??= String(ag.nombre);
  }
  const numero = siguienteNumero(a);
  a.tabla("soporte").unshift({ id: a.nuevoId(), numero, asunto: datos.asunto, origen: datos.origen, agenciaId: datos.agenciaId ?? null, contacto, folio: datos.folio ?? null, prioridad: "normal", asignadoId: null, estado: "nuevo",
    modulo: datos.origen === "agencia" ? "Portal de agencias" : "Sitio web", usuarioId: null, descripcion: datos.mensaje, creadoEn: ahora(), actualizadoEn: ahora() });
  a.guardar();
  return { numero };
}

export function soporteDeLocal(a: Almacen, agenciaId: string) {
  return a.tabla("soporte").filter((t) => t.agenciaId === agenciaId).sort((x, y) => String(y.creadoEn).localeCompare(String(x.creadoEn)))
    .map((t) => ({ numero: String(t.numero), asunto: String(t.asunto), estado: String(t.estado), creadoEn: String(t.creadoEn),
      respuestas: a.tabla("mensajesSoporte").filter((m) => m.ticketId === t.id && !m.interno).sort((x, y) => String(x.creadoEn).localeCompare(String(y.creadoEn))).map((m) => ({ autor: String(m.autor), texto: String(m.texto), creadoEn: String(m.creadoEn) })) }));
}

function accionSoporteLocal(a: Almacen, id: string, nombre: string, d: Record<string, string>, rol: Rol) {
  if (!["asignarme", "responder", "notaInterna", "resolver", "reabrir"].includes(nombre)) return null;
  const t = a.tabla("soporte").find((x) => x.id === id);
  if (!t) throw new Error("No existe");
  const yo = quienEsLocal(a, rol);
  const autor = String(yo?.nombre ?? rol);
  const texto = (d.texto ?? d.nota ?? "").trim();
  const mensaje = (interno: boolean, cuerpo: string) => a.tabla("mensajesSoporte").push({ id: a.nuevoId(), ticketId: id, autor, texto: cuerpo, interno, creadoEn: ahora() });
  t.actualizadoEn = ahora();
  if (nombre === "asignarme") {
    if (!yo) throw new Error("No hay un usuario para tu rol");
    t.asignadoId = yo.id; if (t.estado === "nuevo") t.estado = "en proceso";
    return { mensaje: `${t.numero ?? "La solicitud"} quedó asignada a ti` };
  }
  if (nombre === "responder") {
    if (texto.length < 2) throw new Error("Escribe la respuesta");
    mensaje(false, texto); t.estado = "esperando respuesta"; t.asignadoId ??= yo?.id ?? null;
    return { mensaje: "Respuesta enviada. La solicitud queda esperando respuesta." };
  }
  if (nombre === "notaInterna") {
    if (texto.length < 2) throw new Error("Escribe la nota");
    mensaje(true, texto);
    return { mensaje: "Nota guardada (solo la ve el equipo)" };
  }
  if (nombre === "resolver") {
    if (t.estado === "resuelto") throw new Error("Ya está resuelta");
    if (texto) mensaje(true, `Cierre: ${texto}`);
    t.estado = "resuelto";
    return { mensaje: "Solicitud resuelta" };
  }
  if (t.estado !== "resuelto") throw new Error("Solo se reabre una solicitud resuelta");
  t.estado = t.asignadoId ? "en proceso" : "nuevo";
  return { mensaje: "Solicitud reabierta" };
}
